/**
 * Friendship functional-graph analysis (Fleck: cycles + tails).
 * Mirrors src/showoff/analysis/graph.py at a TS-friendly level.
 */

export type CycleInfo = {
  /** Each cycle as ordered node ids */
  cycles: number[][];
  /** node → cycle index if on a cycle */
  nodeToCycle: Map<number, number>;
};

export function functionalGraphCycles(friends: Int32Array): CycleInfo {
  const n = friends.length;
  const visited = new Uint8Array(n);
  const stackPos = new Int32Array(n).fill(-1);
  const cycles: number[][] = [];
  const nodeToCycle = new Map<number, number>();

  for (let start = 0; start < n; start++) {
    if (visited[start]) continue;
    const path: number[] = [];
    let u = start;
    for (;;) {
      if (stackPos[u]! >= 0) {
        const cyc = path.slice(stackPos[u]!);
        const cid = cycles.length;
        cycles.push(cyc);
        for (const node of cyc) nodeToCycle.set(node, cid);
        break;
      }
      if (visited[u]) break;
      stackPos[u] = path.length;
      path.push(u);
      visited[u] = 1;
      u = friends[u]!;
    }
    for (const node of path) stackPos[node] = -1;
  }

  return { cycles, nodeToCycle };
}

/** Steps along friend edges until a cycle node (tail length). */
export function chainDepths(friends: Int32Array): Int32Array {
  const n = friends.length;
  const { nodeToCycle } = functionalGraphCycles(friends);
  const depths = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    if (nodeToCycle.has(i)) {
      depths[i] = 0;
      continue;
    }
    let steps = 0;
    let u = i;
    const guard = n + 2;
    while (steps < guard && !nodeToCycle.has(u)) {
      u = friends[u]!;
      steps++;
    }
    depths[i] = steps;
  }
  return depths;
}

export type GraphMetrics = {
  nCycles: number;
  nodesOnCycle: number;
  maxCycleLen: number;
  /** Mean depth over all nodes (cycle nodes contribute 0). */
  meanChainDepth: number;
  /** Mean depth over tail nodes only (excludes on-cycle). 0 if none. */
  meanTailDepth: number;
  maxChainDepth: number;
  nTails: number;
};

export function graphMetrics(friends: Int32Array): GraphMetrics {
  const info = functionalGraphCycles(friends);
  const depths = chainDepths(friends);
  let maxCycleLen = 0;
  for (const c of info.cycles) maxCycleLen = Math.max(maxCycleLen, c.length);
  let sum = 0;
  let tailSum = 0;
  let nTails = 0;
  let maxD = 0;
  for (let i = 0; i < depths.length; i++) {
    const d = depths[i]!;
    sum += d;
    if (d > maxD) maxD = d;
    if (!info.nodeToCycle.has(i)) {
      tailSum += d;
      nTails++;
    }
  }
  const n = depths.length;
  return {
    nCycles: info.cycles.length,
    nodesOnCycle: info.nodeToCycle.size,
    maxCycleLen,
    meanChainDepth: n ? sum / n : 0,
    meanTailDepth: nTails ? tailSum / nTails : 0,
    maxChainDepth: maxD,
    nTails,
  };
}

/** @deprecated import from `./metrics` — re-exported for existing call sites */
export { meanLinkDistances } from "./metrics";
